/* eslint-disable object-curly-newline */
/**
 * 游戏逻辑模块
 * @param {object} kaplay - KAPLAY引擎实例
 */
export function createGameScene(kaplay) {
    const { add, sprite, pos, loop, anchor, scale, z, vec2, width, height, lerp, onMouseMove, onUpdate, setCursor } = kaplay;

    // 游戏状态枚举
    const GameState = {
        IDLE: 'idle', // 待机状态
        FOLLOW: 'follow' // 追随状态
    };

    // 当前游戏状态
    let currentState = GameState.IDLE;

    // Cat组位置（屏幕中央偏下）
    const catPosition = vec2(width() / 2, height() * 0.5);

    // 创建Cat组对象（默认状态：catL和catR切换）
    let catGroup = null;
    let catPlay = null;
    let catTeaser = null;

    // 切换动画相关
    let switchTimer = null;
    let isShowingCatL = true;

    // 追随动画相关
    let mousePos = vec2(0, 0);

    /**
     * 创建待机状态的Cat组
     */
    function createIdleCatGroup() {
        if (catGroup) {
            catGroup.destroy();
        }

        catGroup = add([pos(catPosition), anchor('center'), scale(0.9), z(2)]);

        // 添加catL精灵（初始显示）
        const catL = catGroup.add([sprite('catL'), anchor('center'), kaplay.opacity(1)]);

        // 添加catR精灵（初始隐藏）
        const catR = catGroup.add([sprite('catR'), anchor('center'), kaplay.opacity(0)]);

        // 开始切换动画
        startSwitchAnimation(catL, catR, 0.5);
    }

    /**
     * 开始catL和catR的切换动画
     * @param {object} catL - catL精灵对象
     * @param {object} catR - catR精灵对象
     * @param {number} interval - 循环时间
     */
    function startSwitchAnimation(catL, catR, interval = 0.5) {
        if (switchTimer) {
            switchTimer.cancel();
        }

        switchTimer = loop(interval, () => {
            if (currentState !== GameState.IDLE) {
                return;
            }

            const showLeft = !isShowingCatL;
            catL.opacity = showLeft ? 1 : 0;
            catR.opacity = showLeft ? 0 : 1;
            isShowingCatL = showLeft;
        });
    }

    /**
     * 创建追随状态的游戏对象
     */
    function createFollowObjects() {
        // 清理待机对象
        if (catGroup) {
            catGroup.destroy();
        }
        if (switchTimer) {
            switchTimer.cancel();
        }

        // 创建catPlay
        catPlay = add([sprite('catPlay'), pos(catPosition), anchor('center'), scale(0.9), z(3)]);

        // 创建catTeaser（初始位置在catPlay右下方）
        const teaserOffset = vec2(80, -60);
        catTeaser = add([sprite('catTeaser'), pos(catPosition.add(teaserOffset)), anchor('center'), scale(0.8), z(2)]);

        // 设置自定义光标样式
        setCursor('none');
    }

    /**
     * 根据水平位置计算角度偏移
     * @param {number} x - 当前x坐标
     * @param {number} centerX - 屏幕中心x坐标
     * @returns {number} 角度偏移
     */
    function getPositionBasedRotation(x, centerX) {
        const maxAngle = 15; // 最大角度偏移
        const normalizedX = (x - centerX) / (width() / 2); // 归一化到-1到1
        return normalizedX * maxAngle; // 左边负角度，右边正角度
    }

    /**
     * 更新追随逻辑
     */
    function updateFollow() {
        if (currentState !== GameState.FOLLOW || !catPlay || !catTeaser) {
            return;
        }

        // catTeaser跟随鼠标位置
        const teaserTargetPos = mousePos;
        catTeaser.pos = lerp(catTeaser.pos, teaserTargetPos, 0.15);

        // catPlay追随catTeaser，保持相对位置（catPlay在catTeaser左上方）
        const playTargetPos = catTeaser.pos.add(vec2(-60, -220));
        catPlay.pos = lerp(catPlay.pos, playTargetPos, 0.15);

        // 为catTeaser添加基于位置的角度偏移
        const teaserRotation = getPositionBasedRotation(catTeaser.pos.x, width() / 2);
        catTeaser.angle = lerp(catTeaser.angle || 0, teaserRotation, 0.1);

        // 为catPlay添加基于位置的角度偏移
        const playRotation = getPositionBasedRotation(catPlay.pos.x, width() / 2);
        catPlay.angle = lerp(catPlay.angle || 0, playRotation, 0.1);
    }

    /**
     * 初始化游戏场景
     */
    function initGame() {
        // 添加背景
        add([sprite('bg'), pos(0, 0), scale(1.2), anchor('topleft'), z(-1)]);

        // 初始化待机状态
        createIdleCatGroup();
        currentState = GameState.IDLE;

        // 鼠标移动事件
        onMouseMove((newPos) => {
            mousePos = newPos;

            if (currentState === GameState.IDLE) {
                // 从待机状态切换到追随状态
                currentState = GameState.FOLLOW;
                createFollowObjects();
            }
        });

        // 鼠标离开游戏区域事件（通过检测边界模拟）
        onUpdate(() => {
            if (currentState === GameState.FOLLOW) {
                updateFollow();

                // 检测鼠标是否离开游戏区域
                const margin = 50;
                if (mousePos.x < -margin || mousePos.x > width() + margin || mousePos.y < -margin || mousePos.y > height() + margin) {
                    // 切换回待机状态
                    currentState = GameState.IDLE;
                    if (catPlay) {
                        catPlay.destroy();
                    }
                    if (catTeaser) {
                        catTeaser.destroy();
                    }
                    setCursor('pointer');
                    createIdleCatGroup();
                }
            }
        });
    }

    return { initGame };
}
